import React, { Fragment } from 'react';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { MarkAsPaidSummaryInterface } from '@vatos-pas/common';

import ConfirmDialog from 'components/ConfirmDialog';
import { formatCurrency } from 'utils';
import { markAsPaidConfirmMessage } from './helpers';

export const PAID_ROWS_WARNING = 'Totals include rows already marked as Paid.';

export type MarkAsPaidConfirmProps = {
  isOpen: boolean;
  /** Rendered once loaded; the prompt stays closed until then. */
  summary: MarkAsPaidSummaryInterface | null;
  /** Red notes shown under the message, after the paid rows warning. */
  warnings?: string[];
  /** Disables Confirm while the run is saving. */
  loading?: boolean;
  title?: string;
  onConfirm: () => void;
  onClose: () => void;
};

/**
 * Mark as Paid confirmation: the question, red warnings, and the selection's
 * total per contractor, closed by the Grand Total.
 */
export const MarkAsPaidConfirm = ({
  isOpen,
  summary,
  warnings = [],
  loading = false,
  title = 'Mark as Paid',
  onConfirm,
  onClose,
}: MarkAsPaidConfirmProps) => {
  const classes = useStyles();

  if (!summary) return null;

  const notes = summary.hasPaidRows
    ? [PAID_ROWS_WARNING, ...warnings]
    : warnings;

  return (
    <ConfirmDialog
      open={isOpen}
      title={title}
      align="center"
      fullWidth
      className={classes.dialog}
      disabled={loading}
      content={
        <Fragment>
          {markAsPaidConfirmMessage(summary.rowCount)}
          {notes.map(note => (
            <Typography
              key={note}
              component="span"
              display="block"
              variant="body2"
              color="error"
            >
              {note}
            </Typography>
          ))}
        </Fragment>
      }
      onConfirm={onConfirm}
      handleClose={onClose}
    >
      <Table
        size="small"
        aria-label="Contractor summary"
        className={classes.table}
      >
        <TableHead>
          <TableRow>
            <TableCell className={classes.heading}>Contractor</TableCell>
            <TableCell align="right" className={classes.heading}>
              Amount
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {summary.contractors.map((contractor, index) => (
            <TableRow key={`${index}-${contractor.contractorName}`}>
              <TableCell>{contractor.contractorName}</TableCell>
              <TableCell align="right" className={classes.amount}>
                {formatCurrency(contractor.amount)}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell className={classes.grandTotal}>Grand Total</TableCell>
            <TableCell
              align="right"
              className={`${classes.amount} ${classes.grandTotal} ${classes.grandTotalAmount}`}
            >
              {formatCurrency(summary.grandTotal)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </ConfirmDialog>
  );
};

const useStyles = makeStyles(theme => {
  const rule = `1px solid ${theme.palette.text.primary}`;
  return {
    // index.css lets every sm dialog grow to 1600px with !important, which
    // stretches this short table across the screen.
    dialog: {
      '& .MuiDialog-paperWidthSm': {
        maxWidth: '600px !important',
      },
    },
    // Default cell padding pushes the Amount column out of a phone-width dialog.
    table: {
      '& .MuiTableCell-sizeSmall': {
        padding: theme.spacing(1.5, 1),
      },
      '& .MuiTableCell-sizeSmall:first-child': {
        paddingLeft: 0,
      },
      '& .MuiTableCell-sizeSmall:last-child': {
        paddingRight: 0,
        whiteSpace: 'nowrap',
      },
    },
    heading: {
      color: theme.palette.text.secondary,
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      borderBottom: rule,
    },
    // Equal-width digits keep the amounts aligned on the decimal point.
    amount: {
      fontVariantNumeric: 'tabular-nums',
    },
    grandTotal: {
      fontWeight: theme.typography.fontWeightBold,
      borderTop: rule,
      borderBottom: 'none',
    },
    grandTotalAmount: {
      borderBottom: `3px double ${theme.palette.text.primary}`,
    },
  };
});

export default MarkAsPaidConfirm;
